import { useEffect, useState, useMemo } from 'react';
import { RefreshCw, Plus, Trash2, Edit, Shield, CheckCircle, XCircle, ChevronDown, ChevronRight, Lock, ArrowUp, ArrowDown } from 'lucide-react';
import api from '../../services/api';
import ACLModal from './ACLModal';
import ConfirmDialog from '../common/ConfirmDialog';
import { toast } from 'react-hot-toast';
import { isSystemAcl, getAclDescription, getAclPurpose, getProtocolName } from '../../utils/aclUtils';
import { usePermissions } from '../../hooks/usePermissions';

interface ACLRule {
  id: string;
  act: 'permit' | 'deny';
  sip: string;
  sip_mask: string;
  dip: string;
  dip_mask: string;
  sport: string;
  dport: string;
  protocol: number;
  sort_order: number;
}

interface ACLTable {
  id: string;
  deviceId: string;
  name: string;
  interfaces?: string[];
  direction?: 'inbound' | 'outbound'; // VSNP-1011: bind arc (defaults inbound)
  priority?: number; // VSNP-968: cross-ACL evaluation order (lower = first)
  // VSNP-973: actual on-edge interface bindings the agent reports, vs the
  // configured `interfaces` above — lets us flag a configured-but-unbound ACL.
  actualBindings?: { ingress: string[]; egress: string[] };
  aclSessionList: ACLRule[];
}

interface ACLRulesTableProps {
  edgeGuid: string;
  readOnly?: boolean;
  vppAclStatus?: any;
}

const ACLRulesTable = ({ edgeGuid, readOnly = false, vppAclStatus }: ACLRulesTableProps) => {
  const { canCreate, canUpdate, canDelete } = usePermissions();
  const [acls, setAcls] = useState<ACLTable[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedAcl, setExpandedAcl] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAcl, setEditingAcl] = useState<ACLTable | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; aclId: string | null; aclName: string }>({
    isOpen: false,
    aclId: null,
    aclName: '',
  });
  const [deleting, setDeleting] = useState(false);
  // VSNP-1011: group user ACLs by bind direction into Inbound/Outbound tabs.
  const [activeTab, setActiveTab] = useState<'inbound' | 'outbound'>('inbound');

  // Legacy rows without an explicit direction default to inbound.
  const inboundAcls = useMemo(() => acls.filter(a => (a.direction ?? 'inbound') !== 'outbound'), [acls]);
  const outboundAcls = useMemo(() => acls.filter(a => a.direction === 'outbound'), [acls]);
  const visibleAcls = activeTab === 'outbound' ? outboundAcls : inboundAcls;

  const fetchACLs = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/v2/edges/${edgeGuid}/acl`);
      const data = response.data?.data?.items || response.data?.items || [];
      setAcls(data);
      setError(null);
    } catch (err: any) {
      setError('Failed to fetch ACL rules');
      setAcls([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchACLs();
  }, [edgeGuid]);

  // VSNP-968: move a user ACL up/down in evaluation order (mirrors rule
  // reordering). Sends the full new id order to the reorder endpoint, which
  // renumbers priority + triggers a batch sync so the edge rebinds in order.
  const [reordering, setReordering] = useState(false);
  // VSNP-1011: reorder within the active direction tab only. Priority is only
  // meaningful among ACLs on the same interface+arc, so inbound and outbound
  // lists are ordered independently; we send just this tab's ids.
  const handleMoveAcl = async (index: number, direction: 'up' | 'down') => {
    const target = direction === 'up' ? index - 1 : index + 1;
    if (target < 0 || target >= visibleAcls.length) return;
    const nextVisible = [...visibleAcls];
    [nextVisible[index], nextVisible[target]] = [nextVisible[target], nextVisible[index]];
    // Optimistic: rebuild the full list, keeping the other tab's ACLs untouched.
    const otherTab = activeTab === 'outbound' ? inboundAcls : outboundAcls;
    setAcls(activeTab === 'outbound' ? [...otherTab, ...nextVisible] : [...nextVisible, ...otherTab]);
    try {
      setReordering(true);
      await api.post(`/api/v2/edges/${edgeGuid}/acl/reorder`, { order: nextVisible.map(a => a.id) });
      await fetchACLs();
    } catch {
      await fetchACLs(); // revert to server truth on failure
    } finally {
      setReordering(false);
    }
  };

  // Derive system ACLs from VPP status — include only known system ACLs (not user DB ACLs)
  const systemAcls = useMemo(() => {
    if (!vppAclStatus?.rules) return [];
    return vppAclStatus.rules.filter((r: any) => isSystemAcl(r.name));
  }, [vppAclStatus]);

  const handleAddAcl = () => {
    setEditingAcl(null);
    setIsModalOpen(true);
  };

  const handleEditAcl = (acl: ACLTable, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingAcl(acl);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (acl: ACLTable, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteConfirm({ isOpen: true, aclId: acl.id, aclName: acl.name });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm.aclId) return;
    try {
      setDeleting(true);
      await api.delete(`/api/v2/edges/${edgeGuid}/acl/${deleteConfirm.aclId}`);
      toast.success('ACL deleted successfully');
      fetchACLs();
    } catch (error: any) {
      console.error('Failed to delete ACL:', error);
      toast.error(error.response?.data?.error || 'Failed to delete ACL');
    } finally {
      setDeleting(false);
      setDeleteConfirm({ isOpen: false, aclId: null, aclName: '' });
    }
  };

  const toggleExpand = (key: string) => {
    setExpandedAcl(expandedAcl === key ? null : key);
  };

  if (loading) {
    return (
      <div className="card">
        <div className="flex items-center justify-center py-8">
          <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
          <span className="ml-2 text-gray-600">Loading ACL rules...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* User-Configured ACLs */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <Shield className="w-5 h-5 text-primary-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">User-Configured ACLs</h3>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={fetchACLs}
              className="btn-secondary text-sm flex items-center"
            >
              <RefreshCw className="w-4 h-4 mr-1" />
              Refresh
            </button>
            {(!readOnly && canCreate) && (
              <button onClick={handleAddAcl} className="btn-primary text-sm flex items-center">
                <Plus className="w-4 h-4 mr-1" />
                Add ACL
              </button>
            )}
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
            {error}
          </div>
        )}

        {/* VSNP-1011: Inbound / Outbound tabs — each ACL binds to one arc. */}
        <div className="flex items-center border-b border-gray-200 mb-4">
          {([
            { key: 'inbound' as const, label: 'Inbound', count: inboundAcls.length },
            { key: 'outbound' as const, label: 'Outbound', count: outboundAcls.length },
          ]).map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2 -mb-px text-sm font-medium border-b-2 ${
                activeTab === tab.key
                  ? 'border-primary-600 text-primary-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
              <span className="ml-1.5 px-1.5 py-0.5 text-xs rounded bg-gray-100 text-gray-600">{tab.count}</span>
            </button>
          ))}
        </div>

        {visibleAcls.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Shield className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>No {activeTab} user-configured ACLs</p>
            <p className="text-sm mt-1">
              {activeTab === 'inbound'
                ? 'Create an ACL to filter traffic entering an interface'
                : 'Create an ACL to filter traffic leaving a non-WAN interface'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {visibleAcls.map((acl, aclIndex) => (
              <div key={acl.id} className="border rounded-lg overflow-hidden">
                {/* ACL Header */}
                <div
                  className="flex items-center justify-between px-4 py-3 bg-gray-50 cursor-pointer hover:bg-gray-100"
                  onClick={() => toggleExpand(`user-${acl.id}`)}
                >
                  <div className="flex items-center">
                    {expandedAcl === `user-${acl.id}` ? (
                      <ChevronDown className="w-4 h-4 text-gray-400 mr-2" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-gray-400 mr-2" />
                    )}
                    <Shield className="w-4 h-4 text-blue-600 mr-2" />
                    <span className="font-medium text-gray-900">{acl.name}</span>
                    <span className="ml-2 px-1.5 py-0.5 text-xs font-medium rounded bg-blue-100 text-blue-700">User</span>
                    {/* VSNP-968: evaluation order (lower = first); list is sorted by it */}
                    <span
                      className="ml-2 px-1.5 py-0.5 text-xs font-medium rounded bg-gray-100 text-gray-600"
                      title="Evaluation order on the interface — lower runs first"
                    >
                      prio {acl.priority ?? 100}
                    </span>
                    <span className="ml-2 text-sm text-gray-500">
                      ({acl.aclSessionList?.length || 0} rules)
                    </span>
                    {/* VSNP-973: configured interfaces + actual on-edge binding status */}
                    {(() => {
                      const cfg = acl.interfaces || [];
                      const act = [...(acl.actualBindings?.ingress || []), ...(acl.actualBindings?.egress || [])];
                      if (cfg.length === 0) {
                        return (
                          <span className="ml-2 px-1.5 py-0.5 text-xs rounded bg-amber-100 text-amber-700"
                            title="No interface configured — this ACL is not attached and filters nothing">
                            not attached
                          </span>
                        );
                      }
                      return (
                        <span className="ml-2 text-xs text-gray-600"
                          title={`Configured: ${cfg.join(', ')} · On edge: ${act.join(', ') || 'none'}`}>
                          iface: {cfg.join(', ')}
                          {act.length > 0
                            ? <span className="ml-1 text-green-600">● bound</span>
                            : <span className="ml-1 text-amber-600">● not bound on edge</span>}
                        </span>
                      );
                    })()}
                  </div>
                  {!readOnly && (
                    <div className="flex items-center space-x-2">
                      {canUpdate && (
                        <>
                          <button
                            disabled={aclIndex === 0 || reordering}
                            onClick={(e) => { e.stopPropagation(); handleMoveAcl(aclIndex, 'up'); }}
                            className="p-1 text-gray-400 hover:text-blue-600 disabled:opacity-30 disabled:hover:text-gray-400"
                            title="Move up — evaluated earlier"
                          >
                            <ArrowUp className="w-4 h-4" />
                          </button>
                          <button
                            disabled={aclIndex === visibleAcls.length - 1 || reordering}
                            onClick={(e) => { e.stopPropagation(); handleMoveAcl(aclIndex, 'down'); }}
                            className="p-1 text-gray-400 hover:text-blue-600 disabled:opacity-30 disabled:hover:text-gray-400"
                            title="Move down — evaluated later"
                          >
                            <ArrowDown className="w-4 h-4" />
                          </button>
                        </>
                      )}
                      {canUpdate && (
                        <button
                          onClick={(e) => handleEditAcl(acl, e)}
                          className="p-1 text-gray-400 hover:text-blue-600"
                          title="Edit ACL"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                      )}
                      {canDelete && (
                        <button
                          onClick={(e) => handleDeleteClick(acl, e)}
                          className="p-1 text-gray-400 hover:text-red-600"
                          title="Delete ACL"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* ACL Rules (Expanded) */}
                {expandedAcl === `user-${acl.id}` && (
                  <div className="border-t">
                    {!acl.aclSessionList || acl.aclSessionList.length === 0 ? (
                      <div className="px-4 py-3 text-sm text-gray-500">
                        No rules defined in this ACL
                      </div>
                    ) : (
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-2 text-left text-gray-600">#</th>
                            <th className="px-4 py-2 text-left text-gray-600">Action</th>
                            <th className="px-4 py-2 text-left text-gray-600">Protocol</th>
                            <th className="px-4 py-2 text-left text-gray-600">Source</th>
                            <th className="px-4 py-2 text-left text-gray-600">Destination</th>
                            <th className="px-4 py-2 text-left text-gray-600">Ports</th>
                          </tr>
                        </thead>
                        <tbody>
                          {acl.aclSessionList.map((rule, index) => (
                            <tr key={rule.id} className="border-t hover:bg-gray-50">
                              <td className="px-4 py-2 text-gray-500">{index + 1}</td>
                              <td className="px-4 py-2">
                                {rule.act === 'permit' ? (
                                  <span className="flex items-center text-green-600">
                                    <CheckCircle className="w-4 h-4 mr-1" />
                                    Permit
                                  </span>
                                ) : (
                                  <span className="flex items-center text-red-600">
                                    <XCircle className="w-4 h-4 mr-1" />
                                    Deny
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-2 text-gray-700">
                                {getProtocolName(rule.protocol)}
                              </td>
                              <td className="px-4 py-2 font-mono text-gray-700">
                                {rule.sip || 'any'}{rule.sip_mask ? `/${rule.sip_mask}` : ''}
                              </td>
                              <td className="px-4 py-2 font-mono text-gray-700">
                                {rule.dip || 'any'}{rule.dip_mask ? `/${rule.dip_mask}` : ''}
                              </td>
                              <td className="px-4 py-2 text-gray-700">
                                {rule.sport || '*'} → {rule.dport || '*'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* System ACLs (from VPP status) */}
      {systemAcls.length > 0 && (
        <div className="card">
          <div className="flex items-center mb-4">
            <Lock className="w-5 h-5 text-gray-400 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">System ACLs</h3>
            <span className="ml-2 text-sm text-gray-500">(read-only, managed by edge agent)</span>
          </div>

          <div className="space-y-4">
            {systemAcls.map((sysAcl: any) => {
              const entries = sysAcl.entries || [];
              const description = getAclDescription(sysAcl.name);
              const purpose = getAclPurpose(sysAcl.name);
              const expandKey = `sys-${sysAcl.name}`;
              const interfaces = sysAcl.interfaces || {};

              return (
                <div key={sysAcl.name} className="border rounded-lg overflow-hidden">
                  {/* System ACL Header */}
                  <div
                    className="flex items-center justify-between px-4 py-3 bg-gray-50 cursor-pointer hover:bg-gray-100"
                    onClick={() => toggleExpand(expandKey)}
                  >
                    <div className="flex items-center">
                      {expandedAcl === expandKey ? (
                        <ChevronDown className="w-4 h-4 text-gray-400 mr-2" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-gray-400 mr-2" />
                      )}
                      <Shield className="w-4 h-4 text-gray-500 mr-2" />
                      <span className="font-medium text-gray-900">{sysAcl.name}</span>
                      <span className="ml-2 px-1.5 py-0.5 text-xs font-medium rounded bg-gray-100 text-gray-600">System</span>
                      {description && (
                        <span className="ml-2 text-xs text-gray-400" title={purpose || ''}>
                          {description}
                        </span>
                      )}
                      <span className="ml-2 text-sm text-gray-500">
                        ({entries.length} rules)
                      </span>
                    </div>
                  </div>

                  {/* System ACL Rules (Expanded) */}
                  {expandedAcl === expandKey && (
                    <div className="border-t">
                      {/* Interface attachments */}
                      {(interfaces.input?.length > 0 || interfaces.output?.length > 0) && (
                        <div className="px-4 py-2 bg-gray-50 border-b text-xs text-gray-500">
                          {interfaces.input?.length > 0 && (
                            <span>Ingress: {interfaces.input.join(', ')}</span>
                          )}
                          {interfaces.output?.length > 0 && (
                            <span className={interfaces.input?.length > 0 ? ' ml-4' : ''}>
                              Egress: {interfaces.output.join(', ')}
                            </span>
                          )}
                        </div>
                      )}

                      {entries.length === 0 ? (
                        <div className="px-4 py-3 text-sm text-gray-500">
                          No rule details available
                        </div>
                      ) : (
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-2 text-left text-gray-600">#</th>
                              <th className="px-4 py-2 text-left text-gray-600">Action</th>
                              <th className="px-4 py-2 text-left text-gray-600">IP</th>
                              <th className="px-4 py-2 text-left text-gray-600">Protocol</th>
                              <th className="px-4 py-2 text-left text-gray-600">Source</th>
                              <th className="px-4 py-2 text-left text-gray-600">Destination</th>
                              <th className="px-4 py-2 text-left text-gray-600">Ports</th>
                            </tr>
                          </thead>
                          <tbody>
                            {entries.map((entry: any, index: number) => {
                              // VSNP-1009: 3-way action — plain permit (stateless),
                              // permit+reflect (stateful, VPP action 2) and deny.
                              // Check reflect before permit so 'permit_reflect'
                              // isn't mislabelled as a plain permit.
                              const rawAction = entry.action ?? '';
                              const actionKind: 'permit' | 'reflect' | 'deny' =
                                typeof rawAction === 'string'
                                  ? ((rawAction.toLowerCase().includes('reflect') || rawAction.toLowerCase().includes('stateful'))
                                      ? 'reflect'
                                      : rawAction.toLowerCase().includes('permit') ? 'permit' : 'deny')
                                  : (rawAction === 2 ? 'reflect' : rawAction === 1 ? 'permit' : 'deny');
                              const proto = entry.protocol ?? 'any';
                              const src = entry.src_prefix || 'any';
                              const dst = entry.dst_prefix || 'any';
                              // Detect IP version from prefix format
                              const ipVersion = src.includes(':') || dst.includes(':') ? 'v6' : 'v4';
                              // Port range display
                              const srcPortFrom = entry.src_port_from ?? 0;
                              const srcPortTo = entry.src_port_to ?? 0;
                              const dstPortFrom = entry.dst_port_from ?? 0;
                              const dstPortTo = entry.dst_port_to ?? 0;
                              const formatPort = (from: number, to: number) => {
                                if (from === 0 && to === 0) return '*';
                                if (from === 0 && to === 65535) return '*';
                                if (from === to) return String(from);
                                return `${from}-${to}`;
                              };

                              return (
                                <tr key={index} className="border-t hover:bg-gray-50">
                                  <td className="px-4 py-2 text-gray-500">{index + 1}</td>
                                  <td className="px-4 py-2">
                                    {actionKind === 'deny' ? (
                                      <span className="flex items-center text-red-600">
                                        <XCircle className="w-4 h-4 mr-1" />
                                        Deny
                                      </span>
                                    ) : (
                                      <span className="flex items-center text-green-600">
                                        <CheckCircle className="w-4 h-4 mr-1" />
                                        {actionKind === 'reflect' ? 'Permit (stateful)' : 'Permit'}
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-4 py-2">
                                    <span className={`px-1.5 py-0.5 text-xs font-medium rounded ${ipVersion === 'v4' ? 'bg-sky-50 text-sky-700' : 'bg-violet-50 text-violet-700'}`}>
                                      {ipVersion === 'v4' ? 'IPv4' : 'IPv6'}
                                    </span>
                                  </td>
                                  <td className="px-4 py-2 text-gray-700">
                                    {getProtocolName(proto)}
                                  </td>
                                  <td className="px-4 py-2 font-mono text-gray-700 text-xs">
                                    {src}
                                  </td>
                                  <td className="px-4 py-2 font-mono text-gray-700 text-xs">
                                    {dst}
                                  </td>
                                  <td className="px-4 py-2 text-gray-700">
                                    {formatPort(srcPortFrom, srcPortTo)} → {formatPort(dstPortFrom, dstPortTo)}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ACL Modal */}
      <ACLModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={fetchACLs}
        edgeGuid={edgeGuid}
        existingAcl={editingAcl}
        defaultDirection={activeTab}
      />

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false, aclId: null, aclName: '' })}
        onConfirm={handleDeleteConfirm}
        title="Delete ACL"
        message={`Are you sure you want to delete the ACL "${deleteConfirm.aclName}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
        isLoading={deleting}
      />
    </div>
  );
};

export default ACLRulesTable;
